import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { Checkbox, Form, Message, Modal } from "semantic-ui-react";
import { useHistory, useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import queryString from "query-string";
import { availability, checkIfSignUpIsActive, signup, } from "api-operations/auth";
import { getMyUserProfile } from "api-operations/myExfluency/myExfluency";
import ErrorModal from "errors/components/ErrorModal";
import AuthFormLayout from "./AuthFormLayout";
import ValidatedInput from "./ValidatedInput";
import { useEmail, useUsername, useFirstName, useSurname, usePassword, usePasswordConfirmation, usePhoneNumber, } from "./useFieldHooks";
import { StyledForm, StyledButton, ButtonWrapper, FormTitle, AccountLinkText, StyledLink, StyledExternalLink, ErrorMessage, CheckboxContainer, CheckboxLabel, } from "./AuthFormStyles";
import { timezones } from "dictionaries/dictionaries";
const RecaptchaWrapper = styled.div `
  display: flex;
  justify-content: start;
`;
const StyledModal = styled(Modal) `
  z-index: 999;
  border-radius: 25px !important;

  &.ui.modal > .content {
    background-color: #afefff !important;
    border-radius: 25px !important;
    padding: 50px 40px !important;
    text-align: center;
  }
`;
const ModalDimmerStyle = createGlobalStyle `
  .ui.dimmer.modals.page {
    background-color: #01294EB3 !important;
  }
`;
const ModalContentMessage = styled.h2 `
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  color: #01294e;
  margin-bottom: 30px;
`;
const ModalContentMessageDesc = styled.p `
  text-align: center;
  font-size: 18px;
  line-height: 24px;
  color: #01294e;
  margin-bottom: 30px;
  font-weight: 500;
`;
const ModalButtonsRow = styled.div `
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
`;
const ModalButton = styled(StyledButton) `
  margin: 0 !important;

  &.ui.basic.button {
    border-color: #00d2ff !important;
    color: #01294e !important;
    background-color: transparent !important;
    box-shadow: 0 0 0 1px #00d2ff inset !important;
  }
`;
const Signup = () => {
    var _a;
    const location = useLocation();
    const queryParams = queryString.parse(location.search);
    const redirectURL = queryParams.redirectURL;
    const clientId = queryParams.clientId;
    const emailFromQuery = queryParams.email || '';
    const username = useUsername();
    const firstName = useFirstName();
    const surname = useSurname();
    const email = useEmail(emailFromQuery);
    const password = usePassword();
    const passwordConfirmation = usePasswordConfirmation(password.value);
    const phoneNumber = usePhoneNumber();
    const [termsOfService, setTermsOfService] = useState(false);
    const [recaptcha, setRecaptcha] = useState(null);
    const [touchedTermsOfService, setTouchedTermsOfService] = useState(false);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    // Get system timezone or fallback to Europe/London if not matching any of the timezones in the dictionary
    const timezone = timezones.find((t) => t === Intl.DateTimeFormat().resolvedOptions().timeZone) || "Europe/London";
    const [responseStatus, setResponseStatus] = useState("");
    const [errorWindowMessage, setErrorWindowMessage] = useState(null);
    const [signUpActive, setSignUpActive] = useState(true);
    const { t } = useTranslation("common");
    const history = useHistory();
    useEffect(() => {
        checkIfSignupIsPossible();
    }, [signUpActive]);
    useEffect(() => {
        const cookie = document.cookie
            .split(";")
            .some((c) => c.trim().startsWith("ExfluencyCookie="));
        if (cookie) {
            setProfile();
        }
    }, []);
    const checkFieldsValidation = () => {
        if (username.isValid &&
            firstName.isValid &&
            surname.isValid &&
            email.isValid &&
            password.isValid &&
            passwordConfirmation.isValid &&
            phoneNumber.isValid &&
            termsOfService === true &&
            recaptcha !== null) {
            return true;
        }
        return false;
    };
    const checkIfSignupIsPossible = async () => {
        const signupActive = await checkIfSignUpIsActive();
        setSignUpActive(JSON.parse(signupActive));
    };
    const addUser = async () => {
        setLoading(true);
        if (!checkFieldsValidation()) {
            setLoading(false);
            return;
        }
        try {
            const res = await availability({
                login: username.value.toLowerCase(),
                email: email.value.toLowerCase(),
            });
            const { unavailableLogin, unavailableEmail } = res;
            if (unavailableLogin || unavailableEmail) {
                if (unavailableLogin) {
                    username.setErrorMessage(t("accountDescriptions.takenLogin"));
                    username.setTouched(true);
                }
                if (unavailableEmail) {
                    email.setErrorMessage(t("accountDescriptions.takenEmail"));
                    email.setTouched(true);
                }
                setLoading(false);
                return;
            }
        }
        catch (error) {
            setErrorWindowMessage(error);
            return;
        }
        const user = {
            login: username.value.toLowerCase(),
            name: firstName.value,
            surname: surname.value,
            email: email.value.toLowerCase(),
            timezone: timezone,
            password: password.value,
            termsOfService: termsOfService,
            recaptchaToken: recaptcha,
            ...(redirectURL && { redirectUrlAfterVerification: redirectURL }),
            ...(clientId && { clientId: clientId }),
            ...(phoneNumber.value && { phoneNumber: phoneNumber.value }),
        };
        try {
            await signup(user);
            setOpenModal(true);
        }
        catch (error) {
            setResponseStatus("");
            return;
        }
        setLoading(false);
    };
    const onModalClose = () => {
        setOpenModal(false);
        if (redirectURL) {
            window.location.href = redirectURL;
        }
        else {
            history.push("/");
        }
    };
    const setProfile = async () => {
        try {
            const profile = await getMyUserProfile();
            if (profile.roles.includes("ROLE_BUYER")) {
                history.push("/requester");
            }
            else if (profile.roles.includes("ROLE_ENHANCER")) {
                history.push("/enhancer");
            }
            else {
                history.push("/settings");
            }
        }
        catch (error) {
            setErrorWindowMessage(null);
        }
    };
    const handleSubmit = () => {
        addUser();
    };
    const handleRecaptcha = (token) => {
        setRecaptcha(token);
    };
    if (errorWindowMessage !== null) {
        return (React.createElement(ErrorModal, { ...errorWindowMessage, onClose: () => window.location.reload() }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(ModalDimmerStyle, null),
        React.createElement(StyledModal, { closeOnDimmerClick: false, open: openModal, dimmer: "inverted", size: "small" },
            React.createElement(Modal.Content, null,
                React.createElement(Modal.Description, null,
                    React.createElement(ModalContentMessage, null, t("accountDescriptions.sendEmailMessage")),
                    React.createElement(ModalContentMessageDesc, null,
                        t("accountDescriptions.redirect"),
                        React.createElement("br", null),
                        React.createElement("strong", null, email.value)),
                    React.createElement(ModalContentMessageDesc, null,
                        t("accountDescriptions.openMail"),
                        React.createElement("br", null),
                        t("accountDescriptions.checkSpam")),
                    React.createElement(ModalButtonsRow, null,
                        React.createElement(ModalButton, { basic: true, content: "Resend link", onClick: () => history.push("/resend-link") }),
                        React.createElement(ModalButton, { primary: true, content: "Close", onClick: onModalClose }))))),
        React.createElement(AuthFormLayout, { showBottomSection: true, bottomSection: React.createElement(AccountLinkText, null,
                t("settings.haveAccount"),
                " ",
                React.createElement(StyledExternalLink, { href: redirectURL }, "Sign in")) }, !signUpActive ? (React.createElement("div", null, t("settings.signupClosed"))) : (React.createElement(StyledForm, { autoComplete: "off", onSubmit: handleSubmit },
            React.createElement(FormTitle, null, "Create account"),
            React.createElement(ValidatedInput, { type: "text", id: "login", value: username.value, onChange: username.handleChange, placeholder: "Username", errorMessage: username.errorMessage, touched: username.touched, autoFocus: true }),
            React.createElement(ValidatedInput, { type: "text", id: "name", value: firstName.value, onChange: firstName.handleChange, placeholder: "First name", errorMessage: firstName.errorMessage, touched: firstName.touched }),
            React.createElement(ValidatedInput, { type: "text", id: "surname", value: surname.value, onChange: surname.handleChange, placeholder: "Last name", errorMessage: surname.errorMessage, touched: surname.touched }),
            React.createElement(ValidatedInput, { type: "email", id: "email", value: email.value, onChange: email.handleChange, placeholder: "Email", errorMessage: email.errorMessage, touched: email.touched, disabled: !!emailFromQuery }),
            React.createElement(ValidatedInput, { type: "tel", id: "phoneNumber", value: phoneNumber.value, onChange: phoneNumber.handleChange, placeholder: "Phone number (optional)", errorMessage: phoneNumber.errorMessage, touched: phoneNumber.touched }),
            React.createElement(ValidatedInput, { type: "password", id: "password", value: password.value, onChange: password.handleChange, placeholder: "Password", errorMessage: password.errorMessage, touched: password.touched }),
            React.createElement(ValidatedInput, { type: "password", id: "passwordConfirmation", value: passwordConfirmation.value, onChange: passwordConfirmation.handleChange, placeholder: "Confirm password", errorMessage: passwordConfirmation.errorMessage, touched: passwordConfirmation.touched }),
            React.createElement(Form.Field, { error: touchedTermsOfService && !termsOfService, style: { marginTop: "20px" } },
                React.createElement(CheckboxContainer, null,
                    React.createElement(Checkbox, { onChange: () => {
                            setTermsOfService(!termsOfService);
                            setTouchedTermsOfService(true);
                        }, checked: termsOfService }),
                    React.createElement(CheckboxLabel, null,
                        "I agree to the",
                        " ",
                        React.createElement(StyledLink, { target: "_blank", to: "/terms-of-service" }, "Terms of Service"))),
                touchedTermsOfService && !termsOfService && (React.createElement(ErrorMessage, null, "Accept terms to continue"))),
            React.createElement(Form.Field, null,
                React.createElement(RecaptchaWrapper, null,
                    React.createElement(ReCAPTCHA, { sitekey: (_a = process.env.RECAPTCHA_SITE_KEY) !== null && _a !== void 0 ? _a : "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI", onChange: handleRecaptcha }))),
            responseStatus !== "" && (React.createElement(Message, { className: responseStatus === "ERROR" ? "negative" : "", content: t("settings.ERROR") })),
            React.createElement(ButtonWrapper, null,
                React.createElement(StyledButton, { loading: loading, disabled: !checkFieldsValidation(), primary: true, content: "Sign up", type: "submit" })))))));
};
export default Signup;
